import { useAuth } from "@/hooks/useAuth";
import {
  invalidateUserPrivateSettings,
  useUserPrivateSettings,
} from "@/hooks/useUserPrivateSettings";
import { supabase } from "@/lib/supabase";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import React, { createContext, useCallback, useContext, useEffect, useRef } from "react";

interface AdvancedModeContextValue {
  isAdvancedMode: boolean;
  isLoading: boolean;
  toggleAdvancedMode: () => void;
  setAdvancedMode: (enabled: boolean) => void;
}

const AdvancedModeContext = createContext<AdvancedModeContextValue | null>(
  null
);

// Fallback: localStorage Key für Offline-First (wird nur verwendet, wenn DB nicht verfügbar)
const ADVANCED_MODE_STORAGE_KEY = "hustle-advanced-mode";

function readAdvancedModeFromLocalStorage(): boolean {
  try {
    return localStorage.getItem(ADVANCED_MODE_STORAGE_KEY) === "true";
  } catch {
    return false;
  }
}

function writeAdvancedModeToLocalStorage(enabled: boolean): void {
  try {
    if (enabled) {
      localStorage.setItem(ADVANCED_MODE_STORAGE_KEY, "true");
    } else {
      localStorage.removeItem(ADVANCED_MODE_STORAGE_KEY);
    }
  } catch (error) {
    console.warn("Fehler beim Speichern in localStorage:", error);
  }
}

export function AdvancedModeProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const migrationAttemptedRef = useRef<string | null>(null);

  const {
    data: settings,
    isLoading: settingsLoading,
    isError: settingsError,
  } = useUserPrivateSettings(user?.id);

  const isAdvancedMode = user?.id
    ? settings?.advanced_mode === true
    : readAdvancedModeFromLocalStorage();

  // Migration: localStorage → DB (einmal pro User)
  useEffect(() => {
    if (!user?.id || settingsLoading || settingsError) return;
    if (migrationAttemptedRef.current === user.id) return;

    const dbAdvancedMode = settings?.advanced_mode;
    if (dbAdvancedMode !== undefined && dbAdvancedMode !== null) {
      migrationAttemptedRef.current = user.id;
      return;
    }

    const localStorageValue = readAdvancedModeFromLocalStorage();
    if (!localStorageValue) {
      migrationAttemptedRef.current = user.id;
      return;
    }

    migrationAttemptedRef.current = user.id;

    void (async () => {
      try {
        const updatedSettings = {
          ...(settings ?? {}),
          advanced_mode: true,
        };

        await supabase.from("user_private").upsert(
          {
            user_id: user.id,
            settings: updatedSettings,
          },
          {
            onConflict: "user_id",
          }
        );

        invalidateUserPrivateSettings(queryClient, user.id);
      } catch (migrationError) {
        console.warn("Fehler bei Advanced Mode Migration:", migrationError);
      }
    })();
  }, [user?.id, settings, settingsLoading, settingsError, queryClient]);

  const mutation = useMutation({
    mutationFn: async (enabled: boolean) => {
      if (!user?.id) {
        writeAdvancedModeToLocalStorage(enabled);
        return;
      }

      const currentSettings = settings ?? {};
      const updatedSettings = {
        ...currentSettings,
        advanced_mode: enabled,
      };

      const { error: upsertError } = await supabase.from("user_private").upsert(
        {
          user_id: user.id,
          settings: updatedSettings,
        },
        {
          onConflict: "user_id",
        }
      );

      if (upsertError) {
        writeAdvancedModeToLocalStorage(enabled);
        throw upsertError;
      }

      writeAdvancedModeToLocalStorage(enabled);
    },
    onSuccess: () => {
      invalidateUserPrivateSettings(queryClient, user?.id);
    },
  });

  const setAdvancedMode = useCallback(
    (enabled: boolean) => {
      mutation.mutate(enabled);
    },
    [mutation]
  );

  const toggleAdvancedMode = useCallback(() => {
    setAdvancedMode(!isAdvancedMode);
  }, [isAdvancedMode, setAdvancedMode]);

  return (
    <AdvancedModeContext.Provider
      value={{
        isAdvancedMode,
        isLoading: settingsLoading || mutation.isPending,
        toggleAdvancedMode,
        setAdvancedMode,
      }}
    >
      {children}
    </AdvancedModeContext.Provider>
  );
}

export function useAdvancedMode() {
  const context = useContext(AdvancedModeContext);
  if (!context) {
    throw new Error("useAdvancedMode must be used within AdvancedModeProvider");
  }
  return context;
}
